import { useCallback } from 'react'
import toast from 'react-hot-toast'

import { resetComponentsSettings } from 'src/redux/actions/componentsSettingsActions'
import { useAppSelector } from 'src/redux/hooks'
import useDashboardsService from 'src/services/dashboardService'
import useMenusStore from 'src/stores/useMenusStore'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { AppMenuSavedDashboardInterface } from 'src/types/AppMenuInterface'
import { findDashboardsOnMenusFn, setSubProductDashboardsOnMenusFn } from 'src/utils/menuUtils'
import useDispatcher from './useDispatcher'
import useGetDashboardPayload from './useGetDashboardPayload'
import useHasChangeOnPage from './useHasChangeOnPage'
import useProductPage from './useProductPage'
import useQueryParams from './useQueryParams'

const useSaveAsDashboard = () => {
  const { saveNewDashboard } = useDashboardsService()
  const settings = useAppSelector(s => s.componentsSettings.settings)
  const { code: subProductCode, program } = useProductPage()
  const { setQueryParams } = useQueryParams()

  const resetComponentsSettingsDispatcher = useDispatcher(resetComponentsSettings)

  const { handleGetSaveDashboardPayloadData } = useGetDashboardPayload()

  const { setHasChangesOnDashboard } = useHasChangeOnPage()

  const setSelectedDashboard = useSelectedDashboardStore(s => s.setSelectedDashboard)
  const { setMenus, menus } = useMenusStore()

  const handleSaveAsDashboard = useCallback(
    (dashboardName: string) => {
      const payload = handleGetSaveDashboardPayloadData(dashboardName)
      return toast
        .promise(
          saveNewDashboard(payload),
          {
            loading: 'Please wait...',
            success: (
              <span>
                <b>{dashboardName}</b> dashboard successfully created
              </span>
            ),
            error: 'An error occurred. Please retry.',
          },
          { duration: 4000 },
        )
        .then(data => {
          const dashboards = data?.data || []
          const dashboardAdded = dashboards.find(
            d => d.label.toLowerCase() === dashboardName.toLowerCase(),
          )

          if (!dashboardAdded) {
            return
          }

          const oldDashboards = findDashboardsOnMenusFn({
            menus,
            subProductCode,
            programId: program?.toString(),
          })

          const newDashboard: AppMenuSavedDashboardInterface = {
            ...dashboardAdded,
            component_identifier: 'saved_dashboard',
            name: dashboardAdded.label,
            default_dashboard: false,
          }

          const newDashboards = [...oldDashboards, newDashboard]

          resetComponentsSettingsDispatcher(settings, settings)
          setHasChangesOnDashboard(false)
          setMenus(s => ({
            ...s,
            menus: setSubProductDashboardsOnMenusFn({
              menus: s.menus,
              dashboards: newDashboards,
              subProductCode,
              programId: program?.toString(),
            }),
          }))

          setSelectedDashboard(newDashboard)
          setQueryParams({
            dashboard_id: newDashboard.id,
          })

          return data
        })
    },
    [
      handleGetSaveDashboardPayloadData,
      resetComponentsSettingsDispatcher,
      setHasChangesOnDashboard,
      saveNewDashboard,
      setSelectedDashboard,
      setMenus,
      subProductCode,
      program,
      menus,
    ],
  )

  return { handleSaveAsDashboard }
}

export default useSaveAsDashboard
